import type * as React from 'react';
import type { BundledLanguage, Highlighter, PlainTextLanguage } from 'shiki';

import { applyDiffNotation, type DiffNotation } from './notation';
import { DEFAULT_SYNTAX } from './syntaxes';
import { theme } from './theme';

// Performance guard for syntax highlighting: cap per-line tokenization length
// to prevent long code lines from blocking rendering. Values are aligned with
// the thresholds used in the GitBook Open repository.
export const TOKENIZE_MAX_LINE_LENGTH = 400;
export const TOKENIZE_MAX_LINE_LENGTH_SINGLE_LINE = 5000;

export interface TokenizerMetadata {
    isPartiallyHighlighted: boolean;
    partiallyHighlightedLineCount: number;
    tokenizeMaxLineLength: number;
    /** Per-line diff classification derived from in-source `[!code ++]`/`[!code --]` markers. */
    lineNotation: Array<DiffNotation | null>;
}

/**
 * Render a code string into Shiki token lines plus metadata used by GitBook code block renderers.
 */
export function tokenizer(
    highlighter: Highlighter,
    code: string,
    lang: BundledLanguage | PlainTextLanguage
) {
    const tokenizeMaxLineLength = resolveTokenizeMaxLineLength(code);
    const loadedLang = highlighter.getLoadedLanguages().includes(lang) ? lang : DEFAULT_SYNTAX;
    const result = highlighter.codeToTokens(code, {
        lang: loadedLang,
        theme,
        tokenizeMaxLineLength,
    });
    const { tokens, lineNotation } = applyDiffNotation(result.tokens);
    const partiallyHighlightedLineCount = countLinesOverMaxLength(code, tokenizeMaxLineLength);
    return {
        ...result,
        tokens,
        lineNotation,
        isPartiallyHighlighted: partiallyHighlightedLineCount > 0,
        partiallyHighlightedLineCount,
        tokenizeMaxLineLength,
    };
}

/**
 * Style fields preserved from Shiki tokens when rendering syntax highlighted code.
 */
export interface TokenStyle {
    color?: string;
    fontStyle?: number;
}

/**
 * Converts Shiki's compact token style metadata into React inline styles.
 */
export function getTokenStyle(token: TokenStyle): React.CSSProperties {
    const style: React.CSSProperties = {};
    if (token.color) {
        style.color = token.color;
    }
    if (token.fontStyle) {
        if (token.fontStyle & 1) {
            style.fontStyle = 'italic';
        }
        if (token.fontStyle & 2) {
            style.fontWeight = 700;
        }
        if (token.fontStyle & 4) {
            style.textDecoration = 'underline';
        }
        if (token.fontStyle & 8) {
            style.textDecoration = style.textDecoration
                ? `${style.textDecoration} line-through`
                : 'line-through';
        }
    }
    return style;
}

/**
 * Converts Shiki token style metadata into a CSS declaration string for Slate decorations.
 */
export function getTokenStyles(token: TokenStyle) {
    const style = getTokenStyle(token);
    const styles: string[] = [];
    if (style.color) {
        styles.push(`color: ${style.color};`);
    }
    if (style.fontStyle) {
        styles.push(`font-style: ${style.fontStyle};`);
    }
    if (style.fontWeight) {
        styles.push(`font-weight: ${style.fontWeight};`);
    }
    if (style.textDecoration) {
        styles.push(`text-decoration: ${style.textDecoration};`);
    }
    return styles.join(' ');
}

function resolveTokenizeMaxLineLength(code: string): number {
    const lines = code.split(/\r\n|\r|\n/);
    return lines.length === 1 ? TOKENIZE_MAX_LINE_LENGTH_SINGLE_LINE : TOKENIZE_MAX_LINE_LENGTH;
}

function countLinesOverMaxLength(code: string, maxLineLength: number): number {
    return code.split(/\r\n|\r|\n/).filter((line) => line.length > maxLineLength).length;
}
